import {get} from 'lodash';

import {ApolloError} from '../state/graphql/apolloWithAbort';

export function getErrMsgFromError(err: any): string | null {
  const apolloErrMsg = extractErrorMessageFromApolloError(err);
  if (apolloErrMsg) {
    return apolloErrMsg;
  }

  if (typeof err.message === `string` && err.message) {
    return err.message;
  }

  if (typeof err === `string` && err) {
    return err;
  }

  return null;
}

/**
 * Constructs a context that informs the error middleware to propagate errors
 * to the request issuer rather than intercepting them.
 */
export function propagateErrorsContext(): {propagateErrors: boolean} {
  return {
    propagateErrors: true,
  };
}

/**
 * Constructs a context that informs the error middleware to propagate errors
 * to the request issuer rather than intercepting them.
 * Including not forcing a relogin for 401 errors
 */
export function propagateErrorsNoLoginContext(): {
  propagateErrors: boolean;
  noLogin: boolean;
} {
  return {
    propagateErrors: true,
    noLogin: true,
  };
}

export function doNotRetryContext(): {doNotRetry: boolean} {
  return {
    doNotRetry: true,
  };
}

export function extractErrorMessageFromApolloError(
  err: unknown
): string | undefined {
  if (!(err instanceof ApolloError) && !(err instanceof Error)) {
    return;
  }

  const errMsg =
    get(err, 'graphQLErrors[0].message') ??
    get(err, 'networkError.result.errors[0].message') ??
    get(err, 'message');

  return errMsg;
}

// Structured code the backend tags run-filter rejections with (e.g. a non-RE2
// regex in run search). See services/gorilla/pkg/filtering.
export const INVALID_RUN_FILTER_CODE = 'INVALID_RUN_FILTER';

// Structured code the backend tags metric-search rejections with (a non-RE2
// regex in panel search). See services/gorilla/api/resolver/metric_search.go.
export const INVALID_METRIC_SEARCH_CODE = 'INVALID_METRIC_SEARCH';
export const USAGE_LIMIT_EXCEEDED_CODE = 'USAGE_LIMIT_EXCEEDED';

type GraphQLErrorLike = {extensions?: {code?: string}; message?: string};

function extractTaggedErrorMessage(
  err: unknown,
  code: string
): string | undefined {
  // A rejection comes back as HTTP 400 (gerr.ErrInvalid), and Apollo
  // turns any non-2xx into a ServerError before the body's `errors` become
  // graphQLErrors — so the tagged error is under networkError.result.
  const candidates = [
    get(err, 'graphQLErrors'),
    get(err, 'networkError.result.errors'),
  ];
  for (const gqlErrors of candidates) {
    if (!Array.isArray(gqlErrors)) {
      continue;
    }
    const gqlErr = (gqlErrors as GraphQLErrorLike[]).find(
      e => e?.extensions?.code === code
    );
    if (gqlErr != null) {
      return gqlErr.message;
    }
  }
  return undefined;
}

// MySQL rejects filter text that a target column's character set can't
// represent (e.g. emoji searched against a utf8mb3 column) with Error 3988.
// Gorilla returns it as a 422 without a structured code (see
// services/gorilla/mysql/errors.go), so it has to be recognized by message.
const COLLATION_CONVERSION_ERROR_REGEX =
  /Error 3988|Conversion from collation .+ impossible for parameter/;

export const UNSUPPORTED_SEARCH_CHARACTERS_MESSAGE =
  "Your search or filters contain characters that aren't supported here (such as emoji).";

function extractCollationErrorMessage(err: unknown): string | undefined {
  const candidates = [
    get(err, 'graphQLErrors'),
    get(err, 'networkError.result.errors'),
  ];
  for (const gqlErrors of candidates) {
    if (!Array.isArray(gqlErrors)) {
      continue;
    }
    const gqlErr = (gqlErrors as GraphQLErrorLike[]).find(
      e =>
        typeof e?.message === 'string' &&
        COLLATION_CONVERSION_ERROR_REGEX.test(e.message)
    );
    if (gqlErr != null) {
      return UNSUPPORTED_SEARCH_CHARACTERS_MESSAGE;
    }
  }
  const message = get(err, 'message');
  if (
    typeof message === 'string' &&
    COLLATION_CONVERSION_ERROR_REGEX.test(message)
  ) {
    return UNSUPPORTED_SEARCH_CHARACTERS_MESSAGE;
  }
  return undefined;
}

export function extractRunFilterErrorMessage(err: unknown): string | undefined {
  return (
    extractTaggedErrorMessage(err, INVALID_RUN_FILTER_CODE) ??
    extractCollationErrorMessage(err)
  );
}

export function extractMetricSearchErrorMessage(
  err: unknown
): string | undefined {
  return extractTaggedErrorMessage(err, INVALID_METRIC_SEARCH_CODE);
}

export function extractUsageLimitErrorMessage(
  err: unknown
): string | undefined {
  return extractTaggedErrorMessage(err, USAGE_LIMIT_EXCEEDED_CODE);
}

export function extractStatusCodeFromApolloError(err: any): number | undefined {
  const statusCode =
    err?.networkError?.statusCode ?? err?.graphQLErrors?.[0]?.statusCode;

  return statusCode;
}
